import { createContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSubscriberRegistry, useSubscribed } from '../hooks/useSubscribers'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

const fetchSystemStats      = () => fetch(`${API_BASE}/system`).then(r => { if (!r.ok) throw new Error('system offline'); return r.json() })
const fetchDockerContainers = () => import.meta.env.DEV ? Promise.resolve([]) : fetch(`${API_BASE}/system/docker`).then(r => { if (!r.ok) throw new Error('docker offline'); return r.json() })

const SystemContext = createContext(null)
const DockerContext = createContext(null)

/**
 * System stats, polled only while a consumer is mounted: every 10 s for the system
 * card, every minute when only the health badge needs to know /system answers.
 */
export function useSystemData({ everyMs = 10_000 } = {}) {
  const system = useSubscribed(SystemContext, everyMs)
  const docker = useSubscribed(DockerContext, everyMs >= 60_000 ? null : 30_000)
  return useMemo(() => ({
    systemStats:      system?.systemStats ?? null,
    dockerContainers: docker?.dockerContainers ?? [],
    error:            system?.error ?? false,
  }), [system, docker])
}

/** Only whether /system answers — for the service badge. */
export const useSystemHealth = () => useSystemData({ everyMs: 60_000 })

export function SystemProvider({ children }) {
  const sys = useSubscriberRegistry()
  const dock = useSubscriberRegistry()
  const systemQ = useQuery({ queryKey: ['system'], queryFn: fetchSystemStats,      enabled: sys.interval != null,  refetchInterval: sys.interval ?? false })
  const dockerQ = useQuery({ queryKey: ['docker'], queryFn: fetchDockerContainers, enabled: dock.interval != null, refetchInterval: dock.interval ?? false })

  const system = useMemo(() => ({
    subscribe:   sys.subscribe,
    systemStats: systemQ.data ?? null,
    error:       systemQ.isError,
  }), [sys.subscribe, systemQ.data, systemQ.isError])
  const docker = useMemo(() => ({
    subscribe:        dock.subscribe,
    dockerContainers: dockerQ.data ?? [],
  }), [dock.subscribe, dockerQ.data])

  return (
    <SystemContext.Provider value={system}>
      <DockerContext.Provider value={docker}>{children}</DockerContext.Provider>
    </SystemContext.Provider>
  )
}
